/**
 * Part 15 — effective ACL role on a resource.
 *
 * Backed by `GET /api/v1/acl/effective?entity_type=...&entity_id=...`.
 * Components use this to gate UI affordances:
 *
 *   const { role, canAtLeast } = useEffectiveRole('project', projectId)
 *   if (canAtLeast('manager')) <DeleteButton />
 *
 * Returns `null` while loading or when the user holds no grant. In `open`
 * mode (the default) most users won't have an explicit grant — components
 * should fall back to the user's global RBAC permissions in that case.
 */
import { useEffect, useState } from 'react'

import { aclService, type ACLRole, type ACLEntityType } from '../services/permissions'

const ROLE_RANK: Record<ACLRole, number> = {
  viewer: 0,
  contributor: 1,
  manager: 2,
  owner: 3,
}

export interface UseEffectiveRoleResult {
  role: ACLRole | null
  loading: boolean
  /** Returns true iff the effective role is >= `min`. False while loading. */
  canAtLeast: (min: ACLRole) => boolean
  refresh: () => void
}

export function useEffectiveRole(
  entityType: ACLEntityType,
  entityId: number | null | undefined,
): UseEffectiveRoleResult {
  const [role, setRole] = useState<ACLRole | null>(null)
  const [loading, setLoading] = useState<boolean>(entityId != null)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    let cancelled = false
    if (entityId == null) {
      setRole(null)
      setLoading(false)
      return
    }
    setLoading(true)
    aclService
      .myEffectiveRole(entityType, entityId)
      .then((res) => {
        if (cancelled) return
        setRole(res?.role ?? null)
      })
      .catch(() => {
        if (cancelled) return
        // If the lookup fails (e.g. 403 because the user can't even read the
        // resource), treat it as "no role". Components decide how to fall
        // back — usually to global RBAC.
        setRole(null)
      })
      .finally(() => {
        if (cancelled) return
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [entityType, entityId, tick])

  const canAtLeast = (min: ACLRole): boolean => {
    if (role == null) return false
    return ROLE_RANK[role] >= ROLE_RANK[min]
  }

  return {
    role,
    loading,
    canAtLeast,
    refresh: () => setTick((t) => t + 1),
  }
}
